if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          container: ".m-main-product--wrapper",
          slider: ".m-media-gallery__inner",
          sliderPagination: ".swiper-pagination",
          sliderPrevEl: ".swiper-button-prev",
          sliderNextEl: ".swiper-button-next",
          navSlider: ".nav-swiper-container",
          thumbsPrevEl: ".kp-gallery-thumbs-prev",
          thumbsNextEl: ".kp-gallery-thumbs-next",
          medias: [".m-product-media--item"],
          mediaWrapper: ".m-product-media--wrapper",
          mediaZoomIns: [".m-product-media__zoom-in"],
          videos: [".media-video"],
          featuredImage: ".m-product-media",
          galleryList: ".m-media-gallery__list",
          galleryInner: ".m-media-gallery__inner",
        };

        this.navSlider = false;
        this.slider = false;
      }

      get context() {
        return this.dataset.context;
      }

      get layout() {
        return this.dataset.layout;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        if (this.initialized) return;
        this.section = this.closest('product-info');
        this.domNodes = queryDomNodes(this.selectors, this);
        if (!this.section || !this.domNodes.galleryInner || !this.domNodes.galleryList) return;
        this.initialized = true;
        this.listenerController = new AbortController();
        this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.enableVariantGroupImages = this.dataset.enableVariantGroupImages === "true";
        this.onlyMedia = this.dataset.onlyMedia === "true";
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.variantPicker = this.section.querySelector("variant-picker");

        this.setSliderOptions();

        const mobileQuery = window.MinimogTheme?.config?.mediaQueryMobile || "screen and (max-width: 767px)";
        const mql = window.matchMedia(mobileQuery);
        this.mediaQuery = mql;
        this.onMediaLayoutChange = this.updateMediaLayout.bind(this);
        mql.addEventListener('change', this.onMediaLayoutChange);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.preparePhotoswipe();
        };

        this.handleAutoplayVideo();

        this.removeAttribute("data-media-loading");
        this.domNodes.galleryInner.style.opacity = 1;
      }

      updateMediaLayout() {
        const mobileQuery = window.MinimogTheme?.config?.mediaQueryMobile || "screen and (max-width: 767px)";
        const isMobile = window.MinimogTheme?.config?.mqlMobile ?? window.matchMedia(mobileQuery).matches;
        if (isMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      setSliderOptions() {
        const { sliderPagination, sliderNextEl: nextEl, sliderPrevEl: prevEl } = this.domNodes;

        this.sliderOptions = {
          initialSlide: Number(this.dataset.initialMediaIndex) || 0,
          autoHeight: true,
          navigation: { nextEl, prevEl },
          threshold: 2,
          loop: !this.enableVariantGroupImages,
          pagination: {
            el: sliderPagination,
            clickable: true,
            type: "fraction",
          },
          on: {
            init: () => {
              this.domNodes.slider.style.opacity = 1;
              this.domNodes = queryDomNodes(this.selectors, this.section);
              requestAnimationFrame(() => {
                window.dispatchEvent(new Event("resize"));
              });
            }
          }
        };

        this.thumbsOptions = {
          loop: false,
          slidesPerView: 5,
          freeMode: true,
          spaceBetween: 10,
          threshold: 2,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          on: {
            init: () => {
              this.domNodes.navSlider.style.opacity = 1
            }
          }
        };

        switch (this.layout) {
          case 'layout-4':
            break;
          case 'layout-5':
            this.sliderOptions = {
              ...this.sliderOptions,
              watchSlidesProgress: true,
              watchSlidesVisibility: true,
              breakpoints: {
                768: {
                  slidesPerView: 2,
                  slidesPerGroup: 1,
                  spaceBetween: 10,
                },
              }
            }
            break;
          case 'layout-6':
            this.thumbsOptions = {
              ...this.thumbsOptions,
              breakpoints: {
                1024: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                  spaceBetween: 5,
                }
              }
            }
            if (this.hasAttribute('data-desktop-thumbnails')) {
              this.thumbsOptions.navigation = {
                prevEl: this.domNodes.thumbsPrevEl,
                nextEl: this.domNodes.thumbsNextEl,
              };
              this.thumbsOptions.watchOverflow = true;
              this.thumbsOptions.breakpoints[1024].spaceBetween = 8;
              this.thumbsOptions.breakpoints[1024].freeModeSticky = true;
            }
            break;
          case 'layout-7':
            this.sliderOptions = {
              ...this.sliderOptions,
              speed: 500,
              centeredSlides: true,
              breakpoints: {
                768: {
                  slidesPerView: 3
                }
              }
            }
        }
      }

      initSlider() {
        if (typeof this.slider !== 'object') {
          const { slider, navSlider, galleryList } = this.domNodes;
          if (!slider || !galleryList || typeof window.MinimogLibs?.Swiper !== 'function') return;

          // Initialize navigation slider if navSlider exists
          this.navSlider = navSlider ? new MinimogLibs.Swiper(navSlider, this.thumbsOptions) : false;

          if (navSlider) {
            this.sliderOptions.thumbs = {
              swiper: this.navSlider
            };
          }

          // Initialize the main slider
          slider.classList.add('swiper-container');
          galleryList.classList.remove('m:grid');
          galleryList.classList.add('swiper-wrapper');

          this.slider = new MinimogLibs.Swiper(slider, this.sliderOptions);
          if (this.hasAttribute('data-desktop-thumbnails') && this.navSlider) {
            // Only the thumbnail rail is bounded; the main image keeps its own aspect ratio.
            const fitThumbnailRail = () => {
              if (this.navSlider.destroyed || this.navSlider.params.direction !== 'vertical') return;
              const available = Math.max(236, window.innerHeight - 180);
              const rows = Math.max(1, Math.min(this.navSlider.slides.length, Math.floor((available - 88 + 8) / 78)));
              const height = `${rows * 78 - 8}px`;
              if (this.style.getPropertyValue('--kp-thumb-rail-height') === height) return;
              this.style.setProperty('--kp-thumb-rail-height', height);
              this.navSlider.update();
              this.slider.thumbs.update();
            };
            this.slider.on('resize update', fitThumbnailRail);
            fitThumbnailRail();
          }
          // Keep the active slide and immediate neighbours ready for a swipe.
          const warmImages = () => {
            const active = this.slider.activeIndex;
            [active - 1, active, active + 1].forEach(index => {
              this.slider.slides[index]?.querySelectorAll('kp-deferred-product-image').forEach(image => image.load?.());
            });
          };
          warmImages();
          this.slider.on('slideChange', warmImages);

          // Swiper counts partially visible thumbs as visible. Reveal the whole
          // selected thumbnail in the vertical rail, including its border.
          const revealThumbnail = () => requestAnimationFrame(() => {
            const nav = this.navSlider;
            if (!nav || nav.destroyed || !this.slider || this.slider.destroyed || nav.params.direction !== 'vertical') return;
            const index = this.slider.realIndex;
            const thumb = nav.slides[index];
            if (!thumb || !nav.el.clientHeight) return;
            const railBounds = nav.el.getBoundingClientRect();
            const thumbBounds = thumb.getBoundingClientRect();
            if (thumbBounds.top < railBounds.top - 0.5 || thumbBounds.bottom > railBounds.bottom + 0.5) {
              nav.slideTo(index, 0);
            }
          });
          this.slider.on('slideChange resize update', revealThumbnail);
          revealThumbnail();

          // Handle slide change for variant group images
          if (!this.enableVariantGroupImages) this.handleSlideChange();
        }
      }

      destroySlider() {
        const { slider, galleryList } = this.domNodes;

        slider?.classList.remove('swiper-container');
        galleryList?.classList.add('m:grid');
        galleryList?.classList.remove('swiper-wrapper');

        if (typeof this.slider === 'object') {
          this.slider.destroy();
          this.slider = false;
        }
        if (this.navSlider) {
          this.navSlider.destroy();
          this.navSlider = false;
        }
      }

      disconnectedCallback() {
        this.mediaQuery?.removeEventListener('change', this.onMediaLayoutChange);
        this.listenerController?.abort();
        this.removeMediaClick?.();
        this.lightbox?.destroy();
        this.lightbox = null;
        this.zoomReady = null;
        if (this.domNodes) this.destroySlider();
        this.initialized = false;
      }

      handleSlideChange() {
        if (!this.slider || this.slideChangeBound === this.slider) return;
        this.slideChangeBound = this.slider;

        this.slider.on("slideChange", (swiper) => {
          window.pauseAllMedia?.(this);

          const { slides, activeIndex } = swiper;
          const activeSlide = slides[activeIndex];
          if (activeSlide) this.playActiveMedia(activeSlide);

          // Determine visible slides based on layout
          const visibleSlides = [activeIndex];
          if (["layout-5", "layout-7"].includes(this.layout)) {
            visibleSlides.push(activeIndex + 1);
          }

          // Check if any of the visible slides have a model media type
          const hasModelMediaType = visibleSlides.some((index) => {
            const slide = slides[index];
            return slide && slide.dataset.mediaType === "model";
          });

          // Toggle slider's draggable state based on media type
          this.toggleSliderDraggableState(!hasModelMediaType);
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.slider.allowTouchMove !== isDraggable) {
          this.slider.allowTouchMove = isDraggable;
        }
      }

      handleAutoplayVideo() {
        const playVideo = (mediaElement) => {
          const mediaType = mediaElement.dataset.mediaType;
          if (mediaType === "video" || mediaType === "external_video") {
            const deferredMedia = mediaElement.querySelector("deferred-media");
            const autoplay = deferredMedia && deferredMedia.dataset.autoPlay === "true";
            if (autoplay) deferredMedia.loadContent?.(false);
          }
        };

        if (this.slider) {
          const { slides, activeIndex } = this.slider;
          const activeSlide = slides[activeIndex];
          if (activeSlide) playVideo(activeSlide);
        } else {
          this.domNodes.medias.forEach(playVideo);
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector("deferred-media");
        if (!deferredMedia || deferredMedia.dataset.autoPlay !== "true") return;

        const playMedia = (element) => {
          if (!element) return;
          if (element.classList.contains("js-youtube") || element.classList.contains("js-vimeo")) {
            const platform = element.classList.contains("js-youtube") ? "youtube" : "vimeo";
            const param = platform === "youtube" ? "mute=1" : "muted=1";
            const symbol = element.src.includes("?") ? "&" : "?";
            element.src += `${symbol}autoplay=1&${param}`;
          } else {
            element.play?.()?.catch?.(() => {});
          }
        };

        if (!deferredMedia.hasAttribute("loaded")) {
          deferredMedia.loadContent?.(false);
        }

        const deferredElement = deferredMedia.querySelector("video, model-viewer, iframe");
        playMedia(deferredElement);
      }

      preparePhotoswipe() {
        // Load the zoom library on gallery intent; normal image/swipe rendering
        // does not need its 78KB of JavaScript on the critical loading path.
        const load = () => {
          if (this.lightbox) return Promise.resolve();
          if (this.zoomReady) return this.zoomReady;
          if (!window.kpPhotoSwipePromise) {
            window.kpPhotoSwipePromise = window.MinimogLibs?.PhotoSwipeLightbox ? Promise.resolve() : new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = this.dataset.zoomScript;
              script.crossOrigin = 'anonymous';
              script.onload = resolve;
              script.onerror = () => { script.remove(); window.kpPhotoSwipePromise = null; reject(new Error('Image zoom could not load')); };
              document.head.append(script);
            });
          }
          this.zoomReady = window.kpPhotoSwipePromise.then(() => {
            if (!this.lightbox) this.handlePhotoswipe();
          }).catch(error => { this.zoomReady = null; throw error; });
          return this.zoomReady;
        };
        this.addEventListener('pointerover', () => load().catch(() => {}), { once: true, signal: this.listenerController.signal });
        this.addEventListener('focusin', () => load().catch(() => {}), { once: true, signal: this.listenerController.signal });
        this.addEventListener('click', async event => {
          // Keep handling clicks once the library is loaded. The lightbox is
          // created with a dataSource only, so PhotoSwipe binds no click of
          // its own; returning early here made every image click after the
          // hover preload a dead click on desktop (Clarity, 25 Sep-23 Oct
          // 2026: 98 of 322 wall-bed gallery clicks on PC did nothing).
          if (this.lightbox?.pswp) return;
          const media = event.target.closest('.m-product-media--item');
          if (!media || (!media.classList.contains('media-type-image') && !event.target.closest('.m-product-media__zoom-in'))) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.galleryFocusTarget = media.querySelector('.m-product-media__zoom-in');
          media.setAttribute('aria-busy', 'true');
          try {
            await load();
            this.lightbox.loadAndOpen(Number(media.dataset.index) || 0);
          } catch (error) { console.warn(error.message); }
          finally { media.removeAttribute('aria-busy'); }
        }, { capture: true, signal: this.listenerController.signal });
      }

      handlePhotoswipe() {
        if (typeof window.MinimogLibs?.PhotoSwipeLightbox !== 'function') return;
        const medias = [...this.querySelectorAll(".m-product-media--item:not(.swiper-slide-duplicate)")];

        const data = medias.map((media) => {
          const { mediaType, index: id } = media.dataset;

          if (mediaType === "image") {
            if (!media.querySelector(".m-product-media")) return null;
            const {
              mediaSrc: src,
              mediaWidth,
              mediaHeight,
              mediaAlt: alt,
            } = media.querySelector(".m-product-media").dataset;
            return {
              src,
              width: parseInt(mediaWidth),
              height: parseInt(mediaHeight),
              alt,
              id,
            };
          } else {
            return {
              html: `<div class="pswp__${mediaType}">${media.innerHTML}</div>`,
              type: mediaType,
              id,
            };
          }
        }).filter(Boolean);
        if (this.lightbox) {
          this.lightbox.options.dataSource = data;
          return;
        }

        const options = {
          dataSource: data,
          bgOpacity: 1,
          close: false,
          zoom: false,
          arrowNext: false,
          arrowPrev: false,
          counter: false,
          preloader: false,
          returnFocus: false,
          pswpModule: MinimogLibs.PhotoSwipeLightbox.PhotoSwipe,
        };
        this.lightbox = new MinimogLibs.PhotoSwipeLightbox(options);

        const historyId = `gallery-${this.id || this.dataset.sectionId || this.context}`;
        let lastIndex = 0;
        this.lightbox.on('afterInit', () => {
          lastIndex = this.lightbox.pswp?.currIndex ?? 0;
          window.KpOverlayHistory?.open(historyId, {
            show: () => this.lightbox.loadAndOpen(lastIndex),
            hide: () => this.lightbox.pswp?.close(),
          });
        });
        this.lightbox.on('close', () => {
          lastIndex = this.lightbox.pswp?.currIndex ?? lastIndex;
          window.KpOverlayHistory?.closed(historyId);
        });
        this.lightbox.on('destroy', () => {
          const trigger = this.querySelector('.swiper-slide-active .m-product-media__zoom-in') || this.galleryFocusTarget;
          if (trigger?.isConnected) trigger.focus({ preventScroll: true });
        });

        this.lightbox.addFilter("thumbEl", (thumbEl, { id }, index) => {
          return this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`) || thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, { data: { id } }) => {
          const el = this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`);
          return el ? el.src : placeholderSrc;
        });

        this.lightbox.on("change", () => {
          window.pauseAllMedia?.(this);
          if (this.slider && this.lightbox.pswp) {
            const { currIndex } = this.lightbox.pswp;
            const slideMethod = this.enableVariantGroupImages ? "slideTo" : "slideToLoop";
            this.slider[slideMethod](currIndex, 100, false);
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          if (this.lightbox.pswp?.currSlide?.data?.type === "model") {
            e.preventDefault();
          }
        });

        // UI elements
        const uiElements = [
          {
            name: "m-close",
            ariaLabel: this.dataset.galleryClose,
            order: 11,
            isButton: true,
            html: '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',
            onClick: (event, el, pswp) => pswp.close(),
          },
          {
            name: "m-arrowNext",
            ariaLabel: this.dataset.galleryNext,
            order: 12,
            isButton: true,
            html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>',
            onClick: (event, el, pswp) => pswp.next(),
          },
          {
            name: "m-arrowPrev",
            ariaLabel: this.dataset.galleryPrevious,
            order: 10,
            isButton: true,
            html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',
            onClick: (event, el, pswp) => pswp.prev(),
          },
        ];

        this.lightbox.on("uiRegister", () => {
          this.lightbox.pswp.ui.registerElement(uiElements[0]);
          if (!this.onlyMedia) {
            uiElements.slice(1).forEach((element) => this.lightbox.pswp.ui.registerElement(element));
          }
        });

        this.lightbox.init();

        const handleMediaClick = (e, media) => {
          e.preventDefault();
          const isImage = media.classList.contains("media-type-image");
          const isZoomButton = e.target.closest(this.selectors.mediaZoomIns[0]);

          if (isImage || isZoomButton) {
            this.galleryFocusTarget = media.querySelector('.m-product-media__zoom-in');
            const index = Number(media.dataset.index) || 0;
            this.lightbox.loadAndOpen(index);
          }
        };

        this.removeMediaClick?.();
        this.removeMediaClick = addEventDelegate({
          selector: this.selectors.medias[0],
          context: this,
          handler: handleMediaClick,
        });
      }

      setActiveMedia(variant) {
        if (!variant) return;
        if (this.slider) {
          this.setActiveMediaForSlider(variant);
        } else {
          this.handleGalleryMode(variant);
        }
      }

      setActiveMediaForSlider(variant) {
        if (variant.featured_media && this.slider && this.slider.wrapperEl) {
          let slideIndex = variant.featured_media.position || 1;
          this.slider.slideToLoop(slideIndex - 1);
        }
      }

      handleGalleryMode(variant) {
        if (this.context !== "featured-product" && variant && variant.featured_media) {
          const selectedMedia = this.querySelector(`.m-media-gallery__list [data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            this.scrollIntoView(selectedMedia);
          }
        }
      }

      scrollIntoView(selectedMedia) {
        selectedMedia.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  );
}
